import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { api } from "@/lib/api";
import { groupFlavorsByRange } from "@/lib/flavorRanges";
import { useCategories } from "@/context/SiteContentContext";
import { useBrand } from "@/context/PartnerContext";

function FlavorGrid({ flavors, testId }) {
  return (
    <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5" data-testid={testId}>
      {flavors.map((f) => (
        <div key={f.id} className="group overflow-hidden border border-border bg-card transition-colors hover:border-primary" data-testid={`info-flavor-${f.id}`}>
          <div className="relative aspect-square w-full bg-white">
            {f.image_url ? (
              <img src={f.image_url} alt={f.name} className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />
            ) : (
              <div className="h-full w-full" style={{ background: f.accent_color }} />
            )}
            {f.stock <= 0 && (
              <span className="mono absolute right-1.5 top-1.5 rounded-full bg-destructive px-2 py-0.5 text-[9px] font-bold uppercase tracking-widest text-white">Out</span>
            )}
          </div>
          <div className="px-2 py-2 text-center" style={{ borderTop: `3px solid ${f.accent_color}` }}>
            <div className="font-heading text-sm font-semibold leading-tight sm:text-base">{f.name}</div>
          </div>
        </div>
      ))}
    </div>
  );
}

export default function ProductInfo() {
  const cats = useCategories();
  const brand = useBrand();
  const [flavors, setFlavors] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/flavors")
      .then((response) => setFlavors(response.data))
      .finally(() => setLoading(false));
  }, []);
  const { dreamlabs } = groupFlavorsByRange(flavors);
  const premium = flavors.filter((f) => f.range_type === "guest");
  const economy = flavors.filter((f) => f.range_type === "economy");

  return (
    <div className="mx-auto max-w-[1200px] px-6 py-8" data-testid="product-info-page">
      <div className="mx-auto max-w-2xl text-center">
        <div className="mono text-[11px] uppercase tracking-[0.25em] text-primary">Product Info</div>
        <h1 className="mt-1.5 font-heading text-3xl font-black tracking-tighter sm:text-4xl">Our Flavours</h1>
        <p className="mx-auto mt-2 max-w-xl text-sm text-muted-foreground">
          Explore our in-house {brand.name} originals, our {cats.guest.title}, and the budget {cats.economy.title} range — then mix and match them in your box.
        </p>
      </div>

      {loading ? (
        <div className="mt-8 text-center mono text-xs uppercase tracking-widest text-muted-foreground">Loading flavours…</div>
      ) : (
        <div className="mt-8 space-y-10">
          <section aria-labelledby="dreamlabs-originals-heading" data-testid="dreamlabs-originals">
            <div>
              <div className="mono text-[10px] uppercase tracking-[0.22em] text-primary">Made by us</div>
              <h2 id="dreamlabs-originals-heading" className="mt-1 font-heading text-2xl font-black tracking-tight">{brand.name} Originals</h2>
              <p className="mt-1 text-sm text-muted-foreground">Our in-house flavour range, crafted by {brand.name} in the UK.</p>
            </div>
            <FlavorGrid flavors={dreamlabs} testId="flavours-grid" />
          </section>

          {premium.length > 0 && (
            <section aria-labelledby="guest-collection-heading" data-testid="guest-collection">
              <div className="border-l-4 border-primary pl-4">
                <h2 id="guest-collection-heading" className="mt-1 font-heading text-2xl font-black tracking-tight">{cats.guest.title}</h2>
                <p className="mt-1 text-sm text-muted-foreground">Special flavours from selected premium brands, available while stocks last.</p>
              </div>
              <FlavorGrid flavors={premium} testId="guest-flavours-grid" />
            </section>
          )}

          {economy.length > 0 && (
            <section aria-labelledby="economy-heading" data-testid="economy-flavours">
              <div className="border-l-4 border-primary pl-4">
                <h2 id="economy-heading" className="mt-1 font-heading text-2xl font-black tracking-tight">{cats.economy.title}</h2>
                <p className="mt-1 text-sm text-muted-foreground">Our low-cost range — one base, with ×2 and ×3 strength options.</p>
              </div>
              <FlavorGrid flavors={economy} testId="economy-flavours-grid" />
            </section>
          )}
        </div>
      )}

      {/* CTA — right underneath, in eyeshot */}
      <div className="mt-6 flex justify-center">
        <Link to="/build-your-box" data-testid="info-build-cta" className="group inline-flex items-center gap-2 bg-primary px-8 py-4 text-sm font-bold uppercase tracking-wide text-primary-foreground shadow-lg shadow-primary/30 transition-transform hover:-translate-y-0.5">
          Start Your Order <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
        </Link>
      </div>
    </div>
  );
}
